/**
 * Thumbnail URL derivation for listing photos (TECH_DEBT.md R5).
 *
 * Server-side (functions/imageThumbs.js + the autoCompressImages trigger and
 * backfill script) writes a `_thumb640` JPEG beside every pedal photo under
 * images/{uid}/ in Storage. That folder is PUBLIC-READ in storage.rules
 * (`match /images/{userId}/{allPaths=**} { allow read: if true; }`), so the
 * derivative is fetchable at a tokenless download URL — which is why this can
 * be a pure string transform of the original URL with no Firestore changes.
 *
 * The naming rule here MUST match functions/imageThumbs.js `thumbObjectPath`;
 * the two test suites share mirrored fixture cases to pin that.
 *
 * Anything unparseable passes through unchanged — callers can feed this any
 * URL and at worst render the original, never break.
 */

export const THUMB_SUFFIX = '_thumb640';

// Matches the encoded object segment of a Firebase Storage download URL:
//   https://firebasestorage.googleapis.com/v0/b/<bucket>/o/<encoded>?<query>
const STORAGE_URL_RE = /^(https:\/\/firebasestorage\.googleapis\.com\/v0\/b\/[^/]+\/o\/)([^?]+)(\?.*)?$/;

// Extension anchored to the END of the encoded segment. Operates on the
// percent-encoded string directly (%2F separators are untouched) — never
// split on '/'.
const EXT_RE = /^(.*)(\.\w+)$/;

export function thumbUrl(originalUrl) {
  if (typeof originalUrl !== 'string') return originalUrl;

  const urlMatch = originalUrl.match(STORAGE_URL_RE);
  if (!urlMatch) return originalUrl; // non-Storage URL (placeholder, legacy, external)

  const [, prefix, encodedPath, query = ''] = urlMatch;

  // Already a thumb (idempotence) — normalize only.
  const alreadyThumb = new RegExp(`${THUMB_SUFFIX}\\.\\w+$`);
  const extMatch = encodedPath.match(EXT_RE);
  if (!extMatch) return originalUrl; // no derivable extension → server made no thumb either
  const thumbPath = alreadyThumb.test(encodedPath)
    ? encodedPath
    : `${extMatch[1]}${THUMB_SUFFIX}${extMatch[2]}`;

  // Keep alt=media (required to get bytes rather than JSON metadata); drop the
  // token — the thumb has no token and the folder is public-read.
  const params = new URLSearchParams(query.startsWith('?') ? query.slice(1) : query);
  params.delete('token');
  if (!params.has('alt')) params.set('alt', 'media');

  return `${prefix}${thumbPath}?${params.toString()}`;
}
